import React, { useState, useEffect } from "react";
import axios from "axios";

interface PaymentStatusProps {
  receiptData: any;
  onContinue: () => void;
  onRefresh?: (updatedData: any) => void;
  onPrevious?: () => void;
}

export default function PaymentStatus({ receiptData: initialReceiptData, onContinue, onRefresh, onPrevious }: PaymentStatusProps) {
  const [receiptData, setReceiptData] = useState(initialReceiptData);
  const status = receiptData.received !== null ? "Completed" : "Due";
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setReceiptData(initialReceiptData);
  }, [initialReceiptData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Call the same API as Payment Complete button
      const response = await axios.get(
        `${process.env.REACT_APP_ACE_API_Url}/api/ace/receipts?receiptDatabaseId=eq ${receiptData.databaseId}&ncToken=${localStorage.getItem(
          "NCToken",
        )}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          },
        },
      );
      // Assuming the response has the updated receipt data
      const updatedReceipt = response.data.value ? response.data.value[0] : response.data;
      setReceiptData(updatedReceipt);
      if (onRefresh) onRefresh(updatedReceipt);
    } catch (error) {
      console.error("Error refreshing receipt:", error);
      alert("Failed to refresh payment status.");
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusIcon = () => {
    if (status === "Completed") {
      return (
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      );
    } else if (status === "Due") {
      return (
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
            clipRule="evenodd"
          />
        </svg>
      );
    } else {
      return (
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
  };

  return (
    <div className="page-container">
      <div className="page-header mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Payment Status</h1>
            <p className="text-sm text-gray-500">Review your payment status</p>
          </div>
        </div>
      </div>
      <div className="form-grid">
        <div className={`payment-status-card ${status.toLowerCase()} relative`}>
          <button
            className="absolute top-2 right-2 p-1 text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md flex items-center disabled:opacity-50"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh Status"
          >
            {refreshing ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="animate-spin">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  stroke="currentColor"
                />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  stroke="currentColor"
                />
              </svg>
            )}
          </button>
          <div className={`status-badge ${status.toLowerCase()}`}>
            {getStatusIcon()}
            {status}
          </div>
          <div className="status-details">
            <p>
              <strong>Receipt Number:</strong> {receiptData.number}
            </p>
            <p>
              <strong>Due Amount:</strong> ${receiptData.due}
            </p>
            <p>
              <strong>Received Amount:</strong> ${receiptData.received || 0}
            </p>
            <p>
              <strong>Due Date:</strong> {receiptData.dueDate ? new Date(receiptData.dueDate).toLocaleDateString() : "N/A"}
            </p>
            <p>
              <strong>Received Date:</strong> {receiptData.receivedDate ? new Date(receiptData.receivedDate).toLocaleDateString() : "N/A"}
            </p>
            <p>
              <strong>Status:</strong> {receiptData.status}
            </p>
          </div>
        </div>
        <div className="form-actions flex justify-center mt-8">
          <button className="btn btn-secondary" onClick={onPrevious}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
            </svg>
            Previous
          </button>

          <button
            className="btn btn-primary"
            onClick={onContinue}
            disabled={status == "Due"}
            style={{
              opacity: status == "Due" ? 0.5 : 1,
              cursor: status == "Due" ? "not-allowed" : "pointer",
            }}
          >
            Continue
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
